/**
 * Endless TimeRecords journal (table, timeline_compact, daily_summary, embedded).
 * Prefetches the next batch via IntersectionObserver before the
 * sentinel reaches the viewport, so scrolling stays seamless.
 */

let observer = null;
let loading = false;
let observed_sentinel = null;
let current_request = null;
window.timeRecordsJournalLazyGeneration = window.timeRecordsJournalLazyGeneration || 0;

function sentinel_element() {
  return document.querySelector('#time_records_journal .js-time-records-journal-lazy-sentinel');
}

function preload_root_margin() {
  const preload_px = Math.max(1400, Math.round(window.innerHeight * 1.75));
  return `0px 0px ${preload_px}px 0px`;
}

function disconnect_observer() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}

function abort_current_request() {
  if (current_request && current_request.abort) {
    current_request.abort();
  }
  current_request = null;
}

function sentinel_request_data(sentinel, generation) {
  const data = {
    page: sentinel.getAttribute('data-next-page'),
    lazy: 1,
    generation: generation
  };
  ['last-am', 'last-week', 'last-year', 'last-day', 'last-employee-id',
   'journal-is-embedded', 'client', 'lookup-focus', 'journal-style', 'time-range-focus',
   'from', 'show-kurzdoku', 'institution', 'purpose', 'purpose-required', 'with-employee', 'lock-filter',
   'filter-data-attrib', 'filter-data-val'].forEach(function (attr) {
    const value = sentinel.getAttribute('data-' + attr);
    if (value) {
      data[attr.replace(/-/g, '_')] = value;
    }
  });
  const min_max_json = sentinel.getAttribute('data-min-max-json');
  if (min_max_json) {
    data.min_max_json = min_max_json;
  }
  const context = sentinel.getAttribute('data-context');
  if (context) {
    data.context = context;
  }
  return data;
}

function load_next_journal_page(sentinel, extra_data) {
  if (!sentinel || loading) return;

  const next_page = (extra_data && extra_data.page) || sentinel.getAttribute('data-next-page');
  const url = sentinel.getAttribute('data-url');
  if (!next_page || !url) {
    disconnect_observer();
    return;
  }

  loading = true;
  sentinel.setAttribute('data-loading', 'true');
  disconnect_observer();

  const generation = window.timeRecordsJournalLazyGeneration;
  const data = sentinel_request_data(sentinel, generation);
  if (extra_data) {
    Object.keys(extra_data).forEach(function (key) {
      if (extra_data[key] == null || extra_data[key] === '') {
        delete data[key];
      } else {
        data[key] = extra_data[key];
      }
    });
  }
  current_request = $.ajax({
    url: url,
    method: 'GET',
    dataType: 'script',
    cache: false,
    data: data,
    complete: function () {
      if (generation !== window.timeRecordsJournalLazyGeneration) return;
      loading = false;
      current_request = null;
      init_journal_lazy_foundation_dropdowns();
      const next_sentinel = sentinel_element();
      if (!next_sentinel) return;
      next_sentinel.removeAttribute('data-loading');
      if (!next_sentinel.getAttribute('data-next-page')) return;
      observe_sentinel(next_sentinel);
    }
  });
}

// Foundation dropdown hover (timeline info) only works after data-options
// (is_hover) are parsed onto each trigger as data-dropdown-init. The first
// batch gets that from $(document).foundation(); later batches do not.
function init_journal_lazy_foundation_dropdowns() {
  const dropdown_lib = window.Foundation && Foundation.libs && Foundation.libs.dropdown;
  if (!dropdown_lib) return;

  const journal = document.getElementById('time_records_journal');
  if (!journal) return;

  const triggers = journal.querySelectorAll('[data-dropdown]');
  if (!triggers.length) return;

  if (typeof dropdown_lib.data_options !== 'function') {
    if (typeof $ !== 'undefined' && typeof $.fn.foundation === 'function') {
      $(document).foundation('dropdown');
    }
    return;
  }

  for (let i = 0; i < triggers.length; i++) {
    const $trigger = $(triggers[i]);
    if ($trigger.data('dropdown-init')) continue;
    $trigger.data('dropdown-init', $.extend({}, dropdown_lib.settings, dropdown_lib.data_options($trigger)));
  }
}

export function initTimeRecordsJournalLazyDropdowns() {
  init_journal_lazy_foundation_dropdowns();
}

function observe_sentinel(sentinel) {
  disconnect_observer();
  if (!sentinel) return;

  if (typeof IntersectionObserver === 'undefined') {
    load_next_journal_page(sentinel);
    return;
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        load_next_journal_page(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: preload_root_margin(),
    threshold: 0
  });

  observer.observe(sentinel);
}

export function initTimeRecordsJournalLazyLoading() {
  const sentinel = sentinel_element();
  if (!sentinel) {
    abort_current_request();
    disconnect_observer();
    observed_sentinel = null;
    loading = false;
    return;
  }

  if (observed_sentinel === sentinel && (observer || loading)) return;

  abort_current_request();
  window.timeRecordsJournalLazyGeneration = (window.timeRecordsJournalLazyGeneration || 0) + 1;
  observed_sentinel = sentinel;
  loading = false;
  observe_sentinel(sentinel);
}

// Restart lazy loading from page 1, limited to the embedded list filter
// (employee_name / institution_code / client_name). Aborts an in-flight
// unfiltered batch so other names cannot append after a filter click.
export function constrainEmbeddedJournalLazyFilter(data_attrib, data_val) {
  abort_current_request();
  window.timeRecordsJournalLazyGeneration = (window.timeRecordsJournalLazyGeneration || 0) + 1;
  loading = false;
  observed_sentinel = null;

  const sentinel = sentinel_element();
  if (!sentinel) {
    disconnect_observer();
    return;
  }

  if (data_attrib && data_attrib !== 'all') {
    sentinel.setAttribute('data-filter-data-attrib', data_attrib);
    sentinel.setAttribute('data-filter-data-val', data_val == null ? '' : String(data_val));
  } else {
    sentinel.removeAttribute('data-filter-data-attrib');
    sentinel.removeAttribute('data-filter-data-val');
  }

  load_next_journal_page(sentinel, {
    page: 1,
    replace: 1,
    last_am: '',
    last_week: '',
    last_year: '',
    last_day: '',
    last_employee_id: ''
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTimeRecordsJournalLazyLoading);
} else {
  initTimeRecordsJournalLazyLoading();
}
